import { Box, Chip, Grid, InputAdornment, Paper, TextFieldProps, Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import cx from 'classnames';
import { isEmpty } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import FacebookIcon from '../../assets/Svg/FacebookIcon';
import InstagramIcon from '../../assets/Svg/InstagramIcon';
import NoImageIcon from '../../assets/Svg/NoImageIcon';
import TwitterIcon from '../../assets/Svg/TwitterIcon';
import WebIcon from '../../assets/Svg/WebIcon';
import AutoComplete from '../../components/AutoComplete';
import FormTextField, { CommonValidator, IFormTextFieldImperativeHandle } from '../../components/FormTextField';
import { ImageUpload } from '../../components/ImageUpload';
import { RoundedButton } from '../../components/RoundedButton';
import SearchTextField, { ISearchTextField } from '../../components/SearchTextField';
import { SubmitControl } from '../../components/SubmitControl';
import { usePenname, usePennameMutation, usePennameStories } from '../../hooks/queries/usePenname';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import { uploadFile } from '../../services/api/S3';
import settings from '../../services/settings';
import { IPenname, ISuggestionSource, ISuggestionSourceUser, IUser } from '../../services/types';
import MergeDialog from './MergeDialog';
import PennameStoryList from './PennameStoryList';
import PennameValidator from './validator';

interface IProps {
  pennameId?: number;
  onCreate?: (penname: IPenname) => void;
  onUpdate?: (penname: IPenname) => void;
  onMerge?: (penname: IPenname, targetPenname: IPenname) => void;
  onRemove?: (penname: IPenname) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  wrap: {
    paddingLeft: 20,
    paddingRight: 20,
    paddingTop: 16,
    paddingBottom: 16,
    minWidth: 720,
  },

  uploaderWrap: {
    padding: 0,
    width: 'fit-content',
  },

  imageWrap: {
    width: 200,
    height: 200,
    borderRadius: 6,
    borderWidth: 0,
    cursor: 'pointer',
    margin: 0,
  },

  imageDescription: {
    display: 'none',
  },

  imageCaption: {
    fontSize: 12,
    textAlign: 'center',
    color: theme.palette.text.primary,
    opacity: 0.38,
    marginTop: 10,
  },

  textFieldMargin: {
    marginBottom: 32,
  },

  textFieldNarrowMargin: {
    marginBottom: 6,
  },

  chip: {
    width: 'fit-content',
    color: '#a2a2a2',
    backgroundColor: theme.palette.type === 'dark' ? '#f2f2f218' : '#f2f2f2',
    marginRight: 8,
    marginBottom: 4,
  },

  storyList: {
    marginTop: 16,
  },

  roundedButton: {
    width: 150,
    marginBottom: 16,
  },

  deleteButton: {
    color: '#ff2d55',
  },

  snsUrlAdornment: {
    fontSize: '14px',
    paddingLeft: 8,
    color: theme.palette.text.primary,
    opacity: 0.38,
  },
}));

const PennameView = (props: IProps): JSX.Element => {
  const classes = useStyles();

  const { pennameId: currentPennameId, onCreate, onMerge, onRemove } = props;

  const [penname, setPenname] = useState<IPenname | Partial<IPenname>>({});
  const [mergeDialogOpen, setMergeDialogOpen] = useState<boolean>(false);

  const [mode, setMode] = useState<'create' | 'edit' | 'view'>(currentPennameId ? 'view' : 'create');
  const [isPendingImageUpload, setIsPendingImageUpload] = useState<boolean>(false);

  const isEditing = mode !== 'view';

  const confirm = useConfirmDialog();

  const pennameRef = useRef<IFormTextFieldImperativeHandle>();
  const emailRef = useRef<IFormTextFieldImperativeHandle>();
  const bioRef = useRef<IFormTextFieldImperativeHandle>();

  const { data: pennameFromQuery, isLoading: isPennameLoading } = usePenname(currentPennameId);
  const { create, update, merge, remove } = usePennameMutation();
  const { data: storiesFromQuery, isLoading: isStoriesLoading } = usePennameStories(currentPennameId);

  useEffect(() => {
    if (pennameFromQuery) {
      setPenname(pennameFromQuery);
    }
  }, [pennameFromQuery]);

  const handleFileLoaded = async (file: File, blob: Blob, encodedImage: string, extName: string) => {
    setPenname(ps => ({ ...ps, thumbUrl: encodedImage }));

    try {
      setIsPendingImageUpload(true);
      const result = await uploadFile(`image.${extName}`, blob);

      if (result?.data && result?.status === 200) {
        setPenname(ps => ({ ...ps, thumbUrl: result.data.url }));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsPendingImageUpload(false);
    }
  };

  const reset = () => {
    if (mode === 'edit' && pennameFromQuery) {
      setPenname(pennameFromQuery);
      setMode('view');
    } else {
      setPenname({});
    }
  };

  const handleCancelClick = () => {
    const hasChanges = !isEmpty(penname) && pennameFromQuery !== penname;

    if (hasChanges) {
      confirm.open({
        title: 'Wait!',
        text: 'You have unsaved changes. Are you sure you want to discard?',
        onProceed: () => {
          reset();
        },
      });
    } else {
      reset();
    }
  };

  const handleEditClick = () => {
    setMode('edit');
  };

  const handleSaveClick = async () => {
    const result = await (currentPennameId ? update : create).mutateAsync(penname);

    setPenname(mode === 'create' ? {} : { ...penname, ...result });

    if (mode === 'edit') {
      setMode('view');
    }

    if (onCreate) {
      onCreate(result);
    }
  };

  const handleMergeButtonClick = () => {
    setMergeDialogOpen(true);
  };

  const handleDeleteButtonClick = () => {
    confirm.open({
      title: 'Delete Pen Name',
      text: 'Are you sure you want to delete this Pen Name?',
      onProceed: async () => {
        if (!penname.id) {
          return;
        }

        await remove.mutateAsync(penname);
        onRemove?.(penname as IPenname);
      },
    });
  };

  const handleMergeDialogClose = () => {
    setMergeDialogOpen(false);
  };

  const handleMergeDialogMerge = (targetPenname: IPenname) => {
    confirm.open({
      title: 'Merge Pen Name',
      text: 'Are you sure you want to merge the Pen Names?',
      onProceed: async () => {
        if (!penname.id) {
          return;
        }

        await merge.mutateAsync({ penname, targetPennameId: targetPenname.id });
        onMerge?.(penname as IPenname, targetPenname);
      },
    });
  };

  const removeLicensee = (id: number | undefined) => [...(penname.licensees || [])].filter(l => l && l.id !== id);

  const isEditorEmpty = () => !penname.editor;
  const isLicenseeEmpty = () => !penname.licensees || penname.licensees.length === 0;

  const getValidationMessages = () => {
    const messages = [
      ...(pennameRef.current?.getValidationMessages() || []),
      ...(emailRef.current?.getValidationMessages() || []),
      ...(bioRef.current?.getValidationMessages() || []),
    ];

    if (isLicenseeEmpty()) {
      messages.push('Please assign at least 1 account to the Pen Name.');
    }

    if (isEditorEmpty()) {
      messages.push('Please select an account you want to use to edit this Pen Name.');
    }
    return messages;
  };

  const canMerge = !isStoriesLoading && currentPennameId && Boolean(storiesFromQuery?.totalCount);
  const canDelete = !isStoriesLoading && currentPennameId && !Boolean(storiesFromQuery?.totalCount);

  return (
    <>
      {canMerge && <RoundedButton label={'Merge Pen Name'} onClick={handleMergeButtonClick} className={classes.roundedButton} />}
      {canDelete && (
        <RoundedButton label={'Delete Pen Name'} onClick={handleDeleteButtonClick} className={cx(classes.roundedButton, classes.deleteButton)} />
      )}

      <Paper className={classes.wrap}>
        <Grid container>
          <Grid item>
            <Box width={200}>
              <ImageUpload
                onFileLoaded={handleFileLoaded}
                src={penname.thumbUrl}
                readonly={mode === 'view'}
                isPending={isPendingImageUpload}
                placeholderRenderer={() => <NoImageIcon />}
                className={classes.uploaderWrap}
                imageWrapClassName={classes.imageWrap}
                descriptionClassName={classes.imageDescription}
              />
              <Typography className={classes.imageCaption}>Writer&#39;s Photo</Typography>
            </Box>
          </Grid>

          <Grid item>
            <Box
              display="flex"
              flexDirection="column"
              paddingLeft={'24px'}
              paddingRight={'24px'}
              width={'400px'}
              boxSizing="content-box"
              marginBottom={'32px'}
            >
              {currentPennameId && (
                <FormTextField
                  defaultValue={String(currentPennameId)}
                  name="Pen name ID"
                  label="Pen Name ID"
                  isEditing={false}
                  isLoading={isPennameLoading}
                  required={true}
                  disabled={true}
                  fullWidth
                  className={classes.textFieldMargin}
                />
              )}
              <FormTextField
                defaultValue={penname.name}
                name="Pen name"
                label="Writer's Pen Name (required)"
                placeholder="Add Writer's name"
                isEditing={isEditing}
                isLoading={isPennameLoading}
                required={true}
                fullWidth
                validators={[PennameValidator.Length, PennameValidator.NoSpecialCharacters, PennameValidator.NoDoubleSpaces]}
                onValueSubmit={(name: string) => {
                  setPenname(ps => ({ ...ps, name }));
                }}
                ref={pennameRef}
                className={classes.textFieldMargin}
              />
              <FormTextField
                defaultValue={penname.email}
                name="Email"
                label="Writer's Email"
                placeholder={isEditing ? "Add Writer's email" : '-'}
                isEditing={isEditing}
                isLoading={isPennameLoading}
                fullWidth
                validators={[CommonValidator.Email]}
                onValueSubmit={(email: string) => {
                  setPenname(ps => ({ ...ps, email }));
                }}
                ref={emailRef}
                className={classes.textFieldMargin}
              />
              <FormTextField
                defaultValue={penname.bio}
                name="Bio"
                label="Writer's Biography (required)"
                placeholder="Tell your readers a little bit about yourself!"
                isEditing={isEditing}
                isLoading={isPennameLoading}
                validators={[PennameValidator.BiographyRequired]}
                usePopup={true}
                fullWidth
                onValueSubmit={(bio: string) => {
                  setPenname(ps => ({ ...ps, bio }));
                }}
                ref={bioRef}
                className={classes.textFieldMargin}
              />
              <FormTextField
                defaultValue={penname.homepage}
                label="Writer's Website"
                placeholder="The URL of writer's website"
                isEditing={isEditing}
                isLoading={isPennameLoading}
                fullWidth
                onValueSubmit={(homepage: string) => {
                  setPenname(ps => ({ ...ps, homepage }));
                }}
                className={classes.textFieldNarrowMargin}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <WebIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <FormTextField
                defaultValue={penname.facebook}
                placeholder="Facebook username"
                isEditing={isEditing}
                isLoading={isPennameLoading}
                fullWidth
                onValueSubmit={(facebook: string) => {
                  setPenname(ps => ({ ...ps, facebook }));
                }}
                className={classes.textFieldNarrowMargin}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FacebookIcon />
                      <Typography className={classes.snsUrlAdornment} style={penname.facebook ? { opacity: 1 } : {}}>
                        {'https://www.facebook.com/'}
                      </Typography>
                    </InputAdornment>
                  ),
                }}
              />
              <FormTextField
                defaultValue={penname.twitter}
                placeholder="Twitter username"
                isEditing={isEditing}
                isLoading={isPennameLoading}
                fullWidth
                onValueSubmit={(twitter: string) => {
                  setPenname(ps => ({ ...ps, twitter }));
                }}
                className={classes.textFieldNarrowMargin}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <TwitterIcon />
                      <Typography className={classes.snsUrlAdornment} style={penname.twitter ? { opacity: 1 } : {}}>
                        {'https://twitter.com/'}
                      </Typography>
                    </InputAdornment>
                  ),
                }}
              />
              <FormTextField
                defaultValue={penname.instagram || undefined}
                placeholder="Instagram username"
                isEditing={isEditing}
                isLoading={isPennameLoading}
                fullWidth
                onValueSubmit={(instagram: string) => {
                  setPenname(ps => ({ ...ps, instagram }));
                }}
                className={classes.textFieldNarrowMargin}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <InstagramIcon />
                      <Typography className={classes.snsUrlAdornment} style={penname.instagram ? { opacity: 1 } : {}}>
                        {'https://www.instagram.com/'}
                      </Typography>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Grid>

          <Grid item>
            {/* Assign Account */}
            <Box display="flex" flexDirection="column" paddingLeft={'24px'} paddingRight={'24px'} width={'360px'} boxSizing="content-box">
              <Box display="flex" alignItems="center">
                <Typography>Assigned Accounts</Typography>
                <Typography variant="caption" style={{ marginLeft: '6px', color: '#aaaaaa' }}>
                  (required)
                </Typography>
              </Box>
              {isEditing && (
                <AutoComplete
                  type="writer"
                  render={(props: ISearchTextField) => {
                    const { value, ...restProps } = props;
                    return <SearchTextField defaultValue={value as string} placeholder="Search" fullWidth {...restProps} />;
                  }}
                  onSelect={(source: ISuggestionSource) => {
                    const { id, writer_name } = source as ISuggestionSourceUser;
                    const newLicensee: Partial<IUser> = {
                      id: Number(id),
                      username: writer_name[0],
                    };
                    setPenname(ps => ({ ...ps, licensees: [...removeLicensee(Number(id)), newLicensee] }));
                  }}
                  resetFormOnSelect={true}
                />
              )}
              <Box marginTop={'12px'} minHeight={'82px'}>
                {penname.licensees?.map(user => (
                  <Chip
                    key={user.id}
                    label={user.username}
                    onDelete={isEditing ? () => setPenname(ps => ({ ...ps, licensees: removeLicensee(user.id) })) : undefined}
                    onClick={() => window.open(`${settings.getCrewURI()}/users/${user.id}`, '_blank', 'noopener noreferrer')}
                    className={classes.chip}
                  />
                ))}
              </Box>
            </Box>

            {/* Pen Name Edit Account */}
            <Box display="flex" flexDirection="column" paddingLeft={'24px'} paddingRight={'24px'} width={'360px'} boxSizing="content-box">
              <Box display="flex" alignItems="center">
                <Typography>Pen Name Edit Account</Typography>
                <Typography variant="caption" style={{ marginLeft: '6px', color: '#aaaaaa' }}>
                  (required)
                </Typography>
              </Box>
              {isEditing && (
                <AutoComplete
                  type="writer"
                  render={(props: ISearchTextField) => {
                    const { value, ...restProps } = props;
                    return <SearchTextField defaultValue={value as string} placeholder="Search" fullWidth {...restProps} />;
                  }}
                  onSelect={(source: ISuggestionSource) => {
                    const { id, writer_name } = source as ISuggestionSourceUser;
                    const editorId = Number(id);
                    const editor: Partial<IUser> = {
                      id: editorId,
                      username: writer_name[0],
                    };
                    setPenname(ps => ({ ...ps, editorId, editor }));
                  }}
                  resetFormOnSelect={true}
                />
              )}
              <Box marginTop={'12px'} minHeight={'82px'}>
                {penname.editor && (
                  <Chip
                    label={penname.editor.username}
                    onDelete={isEditing ? () => setPenname(ps => ({ ...ps, editor: undefined, editorId: undefined })) : undefined}
                    onClick={() => window.open(`${settings.getCrewURI()}/users/${penname.editor!.id}`, '_blank', 'noopener noreferrer')}
                    className={classes.chip}
                  />
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
        <SubmitControl
          mode={mode}
          submitTitle="SAVE"
          validationMessages={getValidationMessages()}
          pending={create.isLoading}
          onCancelClick={handleCancelClick}
          onEditClick={handleEditClick}
          onSubmitClick={handleSaveClick}
          ConfirmDialogProps={{
            title: mode === 'edit' ? 'Update' : 'Create',
            text: `Would you like to ${mode === 'edit' ? 'save the updated information of this' : 'create'} Pen Name?`,
          }}
        />
      </Paper>

      {currentPennameId && (
        <Box marginTop={'32px'}>
          <Typography variant="h6">Stories</Typography>
          <PennameStoryList pennameId={currentPennameId} className={classes.storyList} />
        </Box>
      )}

      <MergeDialog sourcePennameId={penname.id} open={mergeDialogOpen} onClose={handleMergeDialogClose} onMerge={handleMergeDialogMerge} />
    </>
  );
};

export default PennameView;
